import { useState, useEffect } from 'react'
import { klubbleAPI } from '../api/common'
import useDebounce from './useDebounce'
import { useAsyncError } from './useAsyncError'

type Props = {
  username: string
  userId?: string
  usernameStored?: string
}

const useCheckUsernameAvailable = ({ username, userId, usernameStored }: Props) => {
  const [usernameAvailable, setUsernameAvailable] = useState(true)
  const debouncedUsername = useDebounce({ value: username })
  const throwError = useAsyncError()

  useEffect(() => {
    const abortController = new AbortController()

    async function checkUsernameAvailable() {
      const params = new URLSearchParams({ username: debouncedUsername })
      if (userId) {
        params.set('userid', userId)
      }
      try {
        const res = await klubbleAPI.get<boolean>(`auth/isusernameavailable?${params.toString()}`, {
          signal: abortController?.signal,
        })
        setUsernameAvailable(res?.data)
      } catch (e) {
        throwError(e, 'Error getting username')
      }
    }

    if (debouncedUsername?.length && debouncedUsername !== usernameStored) {
      checkUsernameAvailable()
    } else {
      setUsernameAvailable(true)
    }
    return () => {
      abortController.abort()
    }
  }, [debouncedUsername, usernameStored, userId, throwError])

  return usernameAvailable
}

export default useCheckUsernameAvailable
